import { getAllResourcesForContentTypes } from '@/library/api.ts'
import { defineStore } from 'pinia'
import {
  LibraryContentObject,
  SubjectAlignment,
  LibraryGoal,
  LibraryStrategy,
  LibraryPresentLevel,
  LibraryPresentLevelStudentSummary,
  LibraryResource,
  ContentListResponse,
  LibraryObjectsByType,
  LibraryImpactOfDisability
} from '@/library/library_types'
import { getStrategyTags } from '@/strategies/api'
import { CONTENT_TYPES } from '@/util/access_visibility'
import { useUserStore } from '@/authentication/user.store'
import { isMoreThan30DaysAgo } from '@/util/date'
import { storeToRefs } from 'pinia'
import { filterGoalsBySubject, filterResourcesBySubject, filterStrategiesBySubject, filterPresentLevelsBySubject, filterImpactOfDisabilitiesBySubject } from '@/util/subject_filters'
import { getSubjectLabelByContentType, deduplicateNoSELSubjects } from '@/contentprofile/no_sel_composable'

type RecentContentObjects = {
  recentlySaved: LibraryObjectsByType,
  previouslySaved: LibraryObjectsByType
}

export const useRecentlySavedStore = defineStore('recently_saved', {
  state: () => ({
    loading: true,
    loadError: false,

    contentObjects: {
      recentlySaved: {
        goals: [],
        strategies: [],
        resources: [],
        presentlevels: [],
        impact_of_disabilities: [],
      },
      previouslySaved: {
        goals: [],
        strategies: [],
        resources: [],
        presentlevels: [],
        impact_of_disabilities: [],
      }
    } as RecentContentObjects,

    selectedSubject: '',
    subjects: [] as {
      label: string
      value: string,
      count: number
    }[],
    strategySubjects: [] as string[]
  }),
  actions: {
    async fetchRecentlySavedContent() {
      this.loading = true
      this.resetState()
      const userStore = useUserStore()
      const { userId } = storeToRefs(userStore)
      if (!userId.value || userId.value === '') {
        this.loading = false
        this.loadError = true
        return
      }

      getStrategyTags().then((result) => {
        this.strategySubjects = result['subjects']
      })

      const contentTypes = [
        CONTENT_TYPES.SAVED_GOAL,
        CONTENT_TYPES.SAVED_STRATEGY,
        CONTENT_TYPES.SAVED_SUPER_PAGE,
        CONTENT_TYPES.PRESENTLEVEL,
        CONTENT_TYPES.PRESENTLEVEL_STUDENT_SUMMARY,
        CONTENT_TYPES.RESOURCE,
        CONTENT_TYPES.SAVED_IMPACT_OF_DISABILITY
      ]
      getAllResourcesForContentTypes(userId.value, contentTypes)
        .then(
          (data: ContentListResponse) => {
            data.content.forEach((content: LibraryContentObject) => {
              this.processContentObject(content)
            })
            this.loadError = false
            this.loading = false
          },
          (err: Error) => {
            this.loadError = true
            console.error(err)
            this.loading = false
          }
        )
    },
    processContentObject(content: LibraryContentObject) {
      if (content.content_body_error) {
        // skipping this content if it had an error populating the content object's details
        return
      }
      content.show_folder_name = true
      switch (content.content_type) {
        case CONTENT_TYPES.SAVED_GOAL:
        case CONTENT_TYPES.SAVED_SUPER_PAGE:
          this.processGoalContent(content)
          break
        case CONTENT_TYPES.SAVED_STRATEGY:
          this.processStrategyContent(content)
          break
        case CONTENT_TYPES.PRESENTLEVEL:
          this.processPresentLevelContent(content)
          break
        case CONTENT_TYPES.PRESENTLEVEL_STUDENT_SUMMARY:
          this.processPresentLevelStudentSummaryContent(content)
          break
        case CONTENT_TYPES.RESOURCE:
          this.processResourceContent(content)
          break
        case CONTENT_TYPES.SAVED_IMPACT_OF_DISABILITY:
          this.processImpactOfDisabilityContent(content)
          break
        default:
          break
      }
    },
    processGoalContent(content: LibraryContentObject) {
      if (isMoreThan30DaysAgo(content.created_at)) {
        this.contentObjects.previouslySaved.goals.push(content)
      } else {
        this.contentObjects.recentlySaved.goals.push(content)
      }
      const goalSubjects = (content.content_body as LibraryGoal).subject_alignments?.map(
        (alignment: SubjectAlignment) => alignment.subject
      ) || []
      this.addSubjectsToOptions(goalSubjects, CONTENT_TYPES.GOAL)
    },
    processStrategyContent(content: LibraryContentObject) {
      if (isMoreThan30DaysAgo(content.created_at)) {
        this.contentObjects.previouslySaved.strategies.push(content)
      } else {
        this.contentObjects.recentlySaved.strategies.push(content)
      }
      const strategySubjects = (content.content_body as LibraryStrategy).source_strategy.tags.filter((val) =>
        this.strategySubjects.includes(val)
      ) || []
      this.addSubjectsToOptions(strategySubjects, CONTENT_TYPES.STRATEGY)
    },
    processResourceContent(content: LibraryContentObject) {
      if (isMoreThan30DaysAgo(content.created_at)) {
        this.contentObjects.previouslySaved.resources.push(content)
      } else {
        this.contentObjects.recentlySaved.resources.push(content)
      }
      const resourceSubjects = (content.content_body as LibraryResource).subjects || []
      this.addSubjectsToOptions(resourceSubjects, CONTENT_TYPES.RESOURCE)
    },
    processPresentLevelContent(content: LibraryContentObject) {
      if (isMoreThan30DaysAgo(content.created_at)) {
        this.contentObjects.previouslySaved.presentlevels.push(content)
      } else {
        this.contentObjects.recentlySaved.presentlevels.push(content)
      }
      const presentlevelSubjects =
        (content.content_body as LibraryPresentLevel).selected_subjects || []
      this.addSubjectsToOptions(presentlevelSubjects, CONTENT_TYPES.PRESENTLEVEL)
    },
    processPresentLevelStudentSummaryContent(content: LibraryContentObject) {
      if (isMoreThan30DaysAgo(content.created_at)) {
        this.contentObjects.previouslySaved.presentlevels.push(content)
      } else {
        this.contentObjects.recentlySaved.presentlevels.push(content)
      }
      const subjectLabel = (content.content_body as LibraryPresentLevelStudentSummary).subject_label
      if (subjectLabel) {
        this.addSubjectsToOptions([subjectLabel], CONTENT_TYPES.PRESENTLEVEL_STUDENT_SUMMARY)
      }
    },
    processImpactOfDisabilityContent(content: LibraryContentObject) {
      if (isMoreThan30DaysAgo(content.created_at)) {
        this.contentObjects.previouslySaved.impact_of_disabilities.push(content)
      } else {
        this.contentObjects.recentlySaved.impact_of_disabilities.push(content)
      }
      const impactOfDisabilitySubjects = (content.content_body as LibraryImpactOfDisability).subjects || []
      this.addSubjectsToOptions(impactOfDisabilitySubjects, CONTENT_TYPES.IMPACT_OF_DISABILITY)
    },
    addSubjectsToOptions(subjects: string[], contentType: string = CONTENT_TYPES.GOAL) {
      subjects = deduplicateNoSELSubjects(subjects)
      subjects.forEach((rawSubject: string) => {
        const subject = getSubjectLabelByContentType(contentType, rawSubject)
        if (
          !this.subjects.some(
            (item: { label: string; value: string }) => item.value === subject
          )
        ) {
          this.subjects.push({ value: subject, label: subject, count: 1 })
        } else {
          this.subjects = this.subjects.map((item: { label: string; value: string, count: number }) => {
            if (item.value === subject) {
              return { ...item, count: item.count + 1 }
            } else {
              return item
            }
          })
        }
      })
    },
    resetState() {
      this.contentObjects = {
        recentlySaved: {
          goals: [],
          strategies: [],
          resources: [],
          presentlevels: [],
          impact_of_disabilities: [],
        },
        previouslySaved: {
          goals: [],
          strategies: [],
          resources: [],
          presentlevels: [],
          impact_of_disabilities: [],
        }
      } as RecentContentObjects
      this.selectedSubject = ''
    }
  },
  getters: {
    filteredContentObjects(): RecentContentObjects {
      if (!this.selectedSubject) {
        return this.contentObjects
      }
      return {
        recentlySaved: {
          goals: filterGoalsBySubject(this.contentObjects.recentlySaved.goals, this.selectedSubject),
          strategies: filterStrategiesBySubject(this.contentObjects.recentlySaved.strategies, this.selectedSubject),
          resources: filterResourcesBySubject(this.contentObjects.recentlySaved.resources, this.selectedSubject),
          presentlevels: filterPresentLevelsBySubject(this.contentObjects.recentlySaved.presentlevels, this.selectedSubject),
          impact_of_disabilities: filterImpactOfDisabilitiesBySubject(this.contentObjects.recentlySaved.impact_of_disabilities, this.selectedSubject),
        },
        previouslySaved: {
          goals: filterGoalsBySubject(this.contentObjects.previouslySaved.goals, this.selectedSubject),
          strategies: filterStrategiesBySubject(this.contentObjects.previouslySaved.strategies, this.selectedSubject),
          resources: filterResourcesBySubject(this.contentObjects.previouslySaved.resources, this.selectedSubject),
          presentlevels: filterPresentLevelsBySubject(this.contentObjects.previouslySaved.presentlevels, this.selectedSubject),
          impact_of_disabilities: filterImpactOfDisabilitiesBySubject(this.contentObjects.previouslySaved.impact_of_disabilities, this.selectedSubject),
        },
      }
    },
    recentlySaved(): LibraryObjectsByType {
      return this.filteredContentObjects.recentlySaved
    },
    previouslySaved(): LibraryObjectsByType {
      return this.filteredContentObjects.previouslySaved
    },
    possibleSubjects(): { value: string, label: string }[] {
      const subj = this.subjects.sort((a, b) => b.count - a.count)
      const subjectsToReturn = subj.reduce((accumulator, current) => {
        if (current.value !== '') {
          accumulator.push({ label: current.label, value: current.value })
        }
        return accumulator
      }, [] as { value: string, label: string, separator?: boolean }[])
      subjectsToReturn.unshift({ label: 'All Subjects', value: '', separator: true })
      return subjectsToReturn
    },
    hasPreviouslySaved(): boolean {
      return (
        (this.previouslySaved.goals.length +
          this.previouslySaved.strategies.length +
          this.previouslySaved.resources.length +
          this.previouslySaved.presentlevels.length +
          this.previouslySaved.impact_of_disabilities.length) > 0
      )
    },
    hasRecentlySaved(): boolean {
      return (
        (this.recentlySaved.goals.length +
          this.recentlySaved.strategies.length +
          this.recentlySaved.resources.length +
          this.recentlySaved.presentlevels.length +
          this.recentlySaved.impact_of_disabilities.length) > 0
      )
    },
    hasNoContent(): boolean {
      return !this.hasPreviouslySaved && !this.hasRecentlySaved
    }
  }
})